import React, { useState, useMemo, useEffect } from "react";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import FullScreenPopup from "../../../layout/components/FullScreenPopup/FullScreenPopup";
import { Trans, useTranslation } from "@coworker/locales";
import styles from "./newuserpage.module.css";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { Store } from "../../../shared/types/store";
import { SimpleHeader } from "../../../layout/components/SimpleHeader/SimpleHeader";
import { FixaText } from "../../../shared/wrappers/FixaText/FixaText";
import { useLoggedInUser } from "@coworker/app/src/core/auth/useLoggedInUser";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import {
  filterVirtualStoresByAuthorization,
  useAtkomstCoreStores,
} from "../../hooks/useAtkomstCoreStores";
import FixaInlineMessage from "@coworker/apprestructured/src/shared/wrappers/FixaInlineMessage/FixaInlineMessage";
import InformationCircleIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/InformationCircleIconPath";
import LinkOutIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/LinkOutIconPath";
import FixaSSRIcon from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/FixaSSRIcon";
import { useUserHasRoleOnPlatformOwner } from "../../hooks/useUserHasRoleOnPlatformOwner";
import { UnauthorizedPage } from "../Unauthorized/UnauthorizedPage";
import { reportMessageToSentry } from "@coworker/app/src/hooks/useConfigureSentry";

export const NewUserPage: React.FC = () => {
  const { data: hasPlatformOwnerAuthorization } =
    useUserHasRoleOnPlatformOwner();
  const {
    data: storesResponse,
    isLoading,
    isError,
  } = useAtkomstCoreStores(!!hasPlatformOwnerAuthorization);
  const navigate = useFixaNavigate();
  const { loggedInUser } = useLoggedInUser();
  const { t } = useTranslation();
  const [selectedStore, setSelectedStore] = useState<string>("");

  // If user is already fully provisioned, redirect to home
  useEffect(() => {
    if (loggedInUser.storeId && loggedInUser.teamId) {
      navigate("/home");
    }
  }, [loggedInUser.storeId, loggedInUser.teamId, navigate]);

  const stores = useMemo(() => {
    // Only Platform Owners may pick a Fixa-team virtual store.
    return filterVirtualStoresByAuthorization(
      storesResponse,
      !!hasPlatformOwnerAuthorization
    ).sort((a: Store, b: Store) => a.name.localeCompare(b.name));
  }, [storesResponse, hasPlatformOwnerAuthorization]);

  // Render Unauthorized as a standalone page to avoid nesting it inside
  // NewUserPage layout (which causes double headers and stacked full-screen UI).
  if (!isLoading && !isError && stores.length === 0) {
    reportMessageToSentry(
      "NewUserPage: no accessible stores returned, rendering UnauthorizedPage",
      {
        conditions: {
          isLoading,
          isError,
          storesLength: stores.length,
        },
        uid: loggedInUser.uid,
        storeResponse: storesResponse,
        hasPlatformOwnerAuthorization,
      },
      "warning"
    );
    return <UnauthorizedPage />;
  }

  const handleStoreSelect = (storeId: string) => {
    setSelectedStore(storeId);
  };

  const handleContinue = () => {
    const selectedStoreData = stores.find(
      (store: Store) => store.storeId === selectedStore
    );
    navigate("/newuserteamselection", {
      state: {
        storeId: selectedStore,
        storeName: selectedStoreData?.name ?? "",
        nodeType: selectedStoreData?.nodeType ?? "STO",
      },
    });
  };

  return (
    <div className={styles["new-user-page-wrapper"]}>
      <FullScreenPopup
        actionBarContent={
          !isLoading && (
            <FixaButton
              aria-label={"selectstore"}
              className={styles["action-bar-continue-button"] || ""}
              disabled={!selectedStore}
              text={<Trans>continue2String</Trans>}
              type="primary"
              onClick={handleContinue}
            />
          )
        }
        noPadding
        noAppBar
      >
        <SimpleHeader displayAllScreens />
        <div className={styles["content-wrapper"]}>
          <div className={styles["header-section"]}>
            <FixaText tagName="h1" textStyle="Heading.L">
              {t("selectStoreString")}
            </FixaText>
            <FixaText tagName="p" textStyle="Body.M">
              {t("selectStoreDescriptionString")}
            </FixaText>
          </div>

          {isLoading ? (
            <div className={styles["loading-container"]}>
              <FixaText tagName="p" textStyle="Body.M">
                {t("loadingString")}...
              </FixaText>
            </div>
          ) : isError ? (
            <div className={styles["error-container"]}>
              <FixaText
                tagName="p"
                textStyle="Body.M"
                className={styles["error-message"]}
              >
                {t("errorLoadingStoresString")}
              </FixaText>
            </div>
          ) : (
            <>
              <FixaListView id="storeSelector">
                {stores.map((store: Store) => (
                  <FixaListViewItem
                    id={`store-${store.storeId}`}
                    inset
                    key={store.storeId}
                    title={`${store.name} (${store.storeId})`}
                    control="radiobutton"
                    controlProps={{
                      checked: selectedStore === store.storeId,
                      value: store.storeId,
                    }}
                    onChange={() => handleStoreSelect(store.storeId)}
                  />
                ))}
              </FixaListView>
              <div className={styles["store-disclaimer-wrapper"]}>
                <FixaInlineMessage
                  variant="informative"
                  ssrIcon={InformationCircleIconPath}
                  title={t("missingStoreString")}
                  body={t("missingStoreBodyString")}
                  actions={[
                    {
                      text: (
                        <>
                          {t("myIdentityString")}
                          <FixaSSRIcon paths={LinkOutIconPath} />
                        </>
                      ),
                      onClick: () =>
                        window.open(
                          "https://myidentity.apps.ikea.com/IdentityManager/page.axd?RuntimeFormID=f3a76b77-0c8e-43e1-8163-8eb577d03a70&aeweb_handler=p&aeweb_rp=&wproj=0&uid_accproductgroup=9ceeb9a4-acfe-41cb-91bf-d20e4d1d0974&uid_accproductgroupsecond=9ceeb9a4-acfe-41cb-91bf-d20e4d1d0974&ContextID=VI_ITShop_ProductSelection",
                          "_blank",
                          "noopener,noreferrer"
                        ),
                    },
                  ]}
                />
              </div>
            </>
          )}
        </div>
      </FullScreenPopup>
    </div>
  );
};
